export type EmployeeSlug =
  | "amy"
  | "sarah"
  | "olivia"
  | "david"
  | "nancy"
  | "james"
  | "michael"
  | "sophia"
  | "aria";

export interface Employee {
  slug: EmployeeSlug;
  /* The eight own a desk; Aria owns none, being the interface to all of them. Rendering Aria as a
   * ninth desk would claim it acts on work nobody handed it. Copy counting desks reads
   * `DESK_EMPLOYEES`; copy about the roster reads `EMPLOYEES`. */
  kind: "employee" | "superagent";
  name: string;
  role: string;
  desk: string;
  charter: string;
  /** "What <Name> owns" bullets. */
  owns: string[];
  /** "What stays human" bullets. */
  staysHuman: string[];
  onTheRecord: string;
  /* "Works with" lists two peers and then "Your team — every escalation lands
     with a person you choose." That last line is identical on all eight pages
     and is not an employee, so pages render it themselves. */
  worksWith: EmployeeSlug[];
  title: string;
  description: string;
}

export const EMPLOYEES: Employee[] = [
  {
    slug: "amy",
    kind: "employee",
    name: "Amy",
    role: "AI Admissions Manager",
    desk: "Admissions and growth desk",
    charter: "Turns every family inquiry into a documented, admission-ready outcome.",
    owns: [
      "Every inquiry, answered and qualified: web, phone, email, walk-in.",
      "Tours scheduled around your team's real availability.",
      "Follow-ups that never lapse: every family hears back, every time.",
      "Move-in documents collected and packets prepared for e-signature.",
      "A clean handoff: admission-ready files, on the record.",
    ],
    staysHuman: [
      "The tour itself: a family meets your community through a person.",
      "The care decision: whether your community is the right fit.",
      "The welcome on move-in day.",
    ],
    onTheRecord:
      "Every inquiry, conversation, tour and document Amy touches is written to the system of record, your team sees the full pipeline, and nothing lives in a personal inbox.",
    worksWith: ["sarah", "james"],
    title: "Amy: AI Admissions Manager | PEOPLE AI Workforce",
    description:
      "Turns every family inquiry into a documented, admission-ready outcome. What Amy owns, the work traces, and what stays human.",
  },
  {
    slug: "sarah",
    kind: "employee",
    name: "Sarah",
    role: "AI Front Desk Receptionist",
    desk: "Front desk",
    charter: "Answers every call, message and visitor request, 24/7, no hold music.",
    owns: [
      "Every call answered: day, night, weekend, holiday. No hold music.",
      "Messages and requests routed to the right desk with full context.",
      "Visitors pre-registered and expected before they arrive.",
      "A daily digest so staff start the shift already caught up.",
    ],
    staysHuman: [
      "The conversation that needs a person: Sarah hands off warm, with context.",
      "The face at the door: your team greets everyone who walks in.",
      "Anything clinical: routed to staff immediately, never answered by AI.",
    ],
    onTheRecord:
      "Every interaction Sarah handles (caller, question, request, routing) is logged to the record, so a family never has to repeat themselves and staff never wonder what was said.",
    worksWith: ["amy", "david"],
    title: "Sarah: AI Front Desk Receptionist | PEOPLE AI Workforce",
    description:
      "Answers every call, message and visitor request, 24/7, no hold music. What Sarah owns, the work traces, and what stays human.",
  },
  {
    slug: "olivia",
    kind: "employee",
    name: "Olivia",
    role: "AI Staffing Coordinator",
    desk: "Staffing office",
    charter: "Turns one hiring instruction into scheduled, screened interviews.",
    owns: [
      "Job postings drafted and published from one hiring instruction.",
      "Applications monitored across boards, deduplicated into one pipeline.",
      "Screening completed: availability, credentials, basics confirmed.",
      "Interviews scheduled against your calendar, reminders included.",
    ],
    staysHuman: [
      "Meeting the candidates: the interview is yours.",
      "The hiring decision: Olivia prepares it, you make it.",
      "The first-day welcome that makes someone stay.",
    ],
    onTheRecord:
      "Every posting, applicant, screen and schedule lives on the record, so hiring history compounds instead of restarting with every vacancy.",
    worksWith: ["james", "michael"],
    title: "Olivia: AI Staffing Coordinator | PEOPLE AI Workforce",
    description:
      "Turns one hiring instruction into scheduled, screened interviews. What Olivia owns, the work traces, and what stays human.",
  },
  {
    slug: "david",
    kind: "employee",
    name: "David",
    role: "AI Maintenance Coordinator",
    desk: "Facilities and procurement desk",
    charter: "Most systems log the problem. David prices the fix and readies the order.",
    owns: [
      "Every work order read in context: prior fixes, vendors and prices recalled.",
      "Repair paths priced: whole-unit and failing-part, side by side.",
      "Vendors compared against what you actually paid before.",
      "Carts staged so approval is one decision, not an afternoon.",
    ],
    staysHuman: [
      "The diagnosis on site: a tech confirms the symptom in one pass.",
      "Approving the spend: money moves only on your sign-off.",
      "The repair itself.",
    ],
    onTheRecord:
      "Every ticket, price, vendor and outcome is written back to the record, the next PTAC that fails gets priced against everything this one taught the system.",
    worksWith: ["nancy"],
    title: "David: AI Maintenance Coordinator | PEOPLE AI Workforce",
    description:
      "Most systems log the problem. David prices the fix and readies the order. What David owns, the work traces, and what stays human.",
  },
  {
    slug: "nancy",
    kind: "employee",
    name: "Nancy",
    role: "AI Procurement Manager",
    desk: "Facilities and procurement desk",
    charter:
      "Builds the list from the menu, then the cheapest cart across every store, about 30% off the food bill.",
    owns: [
      "The week's list sized from the menu, the recipes and the head count, no over-ordering, less waste.",
      "Usage history pulled across every community before anything is bought.",
      "Every source priced: distributor, retail, manufacturer-direct.",
      "The cheapest compliant cart built, every time.",
      "Orders staged for approval with the comparison attached.",
    ],
    staysHuman: [
      "Approving the spend: carts wait for a person.",
      "Clinical substitutions: a brand change needs the DON's sign-off.",
      "The vendor relationship you want to keep personal.",
    ],
    onTheRecord:
      "Every order Nancy stages lands on the record with its line prices and the comparison it won, so your purchasing history becomes leverage instead of a filing cabinet.",
    worksWith: ["david"],
    title: "Nancy: AI Procurement Manager | PEOPLE AI Workforce",
    description:
      "Builds the list from the menu, then the cheapest cart across every store, about 30% off the food bill. What Nancy owns, the work traces, and what stays human.",
  },
  {
    slug: "james",
    kind: "employee",
    name: "James",
    role: "AI Billing Specialist",
    desk: "Billing office",
    charter: "Reconciles every invoice against the record: no month-end weekend.",
    owns: [
      "Invoices generated from the record: care levels, ancillaries, adjustments.",
      "Payments matched and the rent roll reconciled continuously.",
      "Discrepancies surfaced the day they appear, not at month-end.",
      "Statements prepared and sent on schedule.",
    ],
    staysHuman: [
      "The judgment call on every correction and write-off.",
      "The conversation with a family about a bill.",
      "The close decision: James prepares it, the administrator signs it.",
    ],
    onTheRecord:
      "Every invoice, match, discrepancy and correction is on the record, the month closes from a position of proof, not archaeology.",
    worksWith: ["amy", "michael"],
    title: "James: AI Billing Specialist | PEOPLE AI Workforce",
    description:
      "Reconciles every invoice against the record: no month-end weekend. What James owns, the work traces, and what stays human.",
  },
  {
    slug: "michael",
    kind: "employee",
    name: "Michael",
    role: "AI Compliance Officer",
    desk: "Compliance office",
    charter: "Keeps every chart, log and workflow survey-ready, year-round.",
    owns: [
      "Charts, logs and workflows audited continuously against your framework.",
      "Gaps surfaced and routed to the person who owns the fix.",
      "Fixes verified: a correction counts only when it lands in the record.",
      "Evidence assembled so the binder exists before anyone asks for it.",
    ],
    staysHuman: [
      "The survey itself: surveyors meet your team.",
      "The standards you set: Michael enforces them, you own them.",
      "Every corrective decision with clinical weight.",
    ],
    onTheRecord:
      "Compliance stops being a scramble because the evidence was written down as the work happened, Michael's whole job is keeping that true.",
    worksWith: ["james", "sophia"],
    title: "Michael: AI Compliance Officer | PEOPLE AI Workforce",
    description:
      "Keeps every chart, log and workflow survey-ready, year-round. What Michael owns, the work traces, and what stays human.",
  },
  {
    slug: "sophia",
    kind: "employee",
    name: "Sophia",
    role: "AI Resident Care Assistant",
    desk: "Care documentation",
    charter: "Documents care in the moment, so staff stay with the resident.",
    owns: [
      "Care documented in the moment: dictated, transcribed, filed.",
      "Incident records built from what actually happened, as it happened.",
      "The eMAR kept current: med-pass charting, MAR reports, PRN and controlled-substance documentation.",
      "Charts kept current: care plans, ADLs, notes, dispositions.",
      "Follow-ups prepared so staff walk in knowing what's next.",
    ],
    staysHuman: [
      "The care itself: always, entirely, yours.",
      "Emergency response: people act, Sophia documents.",
      "The reassurance: a staff member calls the family, Sophia logs it.",
    ],
    onTheRecord:
      "Sophia exists so the record reflects the care your team actually gives, without stealing the hours that care needs.",
    worksWith: ["sarah", "michael"],
    title: "Sophia: AI Resident Care Assistant | PEOPLE AI Workforce",
    description:
      "Documents care in the moment, so staff stay with the resident. What Sophia owns, the work traces, and what stays human.",
  },
  {
    slug: "aria",
    kind: "superagent",
    name: "Aria",
    role: "AI Superagent",
    desk: "Over whatever screen you are on",
    charter:
      "Your agent inside the operating system: reads, writes and analyzes for any user, as that user, at the speed of a pro.",
    owns: [
      "Everything your login can see, asked for in a sentence from wherever you already are, residents, care plans, incidents, medications, orders, leads, tours and tasks, without losing your place.",
      "Counts, lists and trends from your own record, with no new report and no spreadsheet.",
      "The right screen for the question: a table, a chart or a set of cards, built for what you asked.",
      "The changes staff make most, drafted whole, a note, an incident, a lead update, a task, in exactly the fields the screen would take.",
      "The situation, every time: today's date, your timezone and the communities you cover, so “overdue” and “this week” mean what you mean.",
      "Speak to it, type to it, or show it a photo or a form; get words, a table, a chart or a filled-in screen back.",
      "On your phone, in the browser or at the office computer, the same Aria and the same record.",
      "A memory of how you work, kept for you alone.",
    ],
    staysHuman: [
      "The button. Every change is shown to you first, with how to undo it; nothing lands until you confirm.",
      "Anything with no way back: no medication administration and no financial posting through Aria.",
      "The authority. Aria works as you, with your access and your security controls, and has no account of its own.",
    ],
    onTheRecord:
      "Everything Aria helps you write is stamped as your work, never Aria's: the record stays yours, and every line says so.",
    worksWith: ["sophia", "michael"],
    title: "Aria: AI Superagent | PEOPLE AI Workforce",
    description:
      "Your agent inside the operating system: reads, writes and analyzes for any user, as that user. What Aria does for you, what stays yours, and how it keeps to your access.",
  },
];

/* The eight, for copy that counts desks or prices them. Derived rather than a
   second list, because two hand-kept rosters drift and the disagreement would
   land on the page that exists to say who works for you. */

export const DESK_EMPLOYEES: Employee[] = EMPLOYEES.filter((e) => e.kind === "employee");
